// Estado do cliente para a partida atual, alimentado pelas mensagens da sala.
import type { Room } from "colyseus.js";
import { buzz, play } from "../audio/sfx";
import type { Action } from "@shared/match/types";
import type { EndInfo, LobbyInfo, PlayerView, RoundResult } from "./types";

type Listener = () => void;

export interface RoomChat { from: string; userId?: string | null; nickname: string; avatar: number | null; text: string; at: number }

export class GameStore {
  lobby: LobbyInfo | null = null;
  view: PlayerView | null = null;
  round: RoundResult | null = null;
  ended: EndInfo | null = null;
  myId: string | null = null;
  started = false;
  lastError: string | null = null;
  disconnected = false;
  /** id estável da partida (para retomar se o nó do jogo reiniciar) */
  matchId: string | null = null;
  migrating = false;
  private listeners = new Set<Listener>();
  private roundListeners = new Set<(r: RoundResult) => void>();
  /** chat da sala (fila e partida), últimas 50 mensagens */
  chat: RoomChat[] = [];
  private chatListeners = new Set<(m: RoomChat) => void>();

  constructor(public room: Room) {
    room.onMessage("welcome", (m: { playerId: string }) => {
      this.lobbyId = m.playerId;
      this.myId = m.playerId;
      this.emit();
    });
    room.onMessage("lobby", (m: LobbyInfo) => {
      this.lobby = m;
      this.emit();
    });
    room.onMessage("started", (m: { matchId?: string; seats: { id: string }[] }) => {
      this.started = true;
      if (m.matchId) this.matchId = m.matchId;
      this.emit();
    });
    room.onMessage("view", (v: PlayerView) => {
      this.view = v;
      this.started = true;
      if (v.me) this.myId = v.me.id;
      this.emit();
    });
    room.onMessage("player", (p: { id: string; hp: number; level: number; board: PlayerView["players"][number]["board"]; alive: boolean }) => {
      const target = this.view?.players.find((x) => x.id === p.id);
      if (target) Object.assign(target, p);
      this.emit();
    });
    room.onMessage("round", (r: RoundResult) => {
      this.round = r;
      if (this.view) this.view.roomPhase = "combat";
      this.roundListeners.forEach((l) => l(r));
      this.emit();
    });
    room.onMessage("ended", (e: EndInfo) => {
      this.ended = e;
      const mine = e.placements.find((p) => p.id === this.myId);
      const top = mine && mine.placement !== null && mine.placement <= Math.max(1, Math.floor(e.placements.length / 2));
      play(top ? "victory" : "defeat");
      buzz(top ? [60, 40, 60, 40, 120] : 200);
      this.emit();
    });
    room.onMessage("error", (e: { error: string }) => {
      this.lastError = e.error;
      if (e.error !== "rate-limited") {
        play("error");
        buzz(40);
      }
      this.emit();
    });
    room.onMessage("cancelled", () => {
      this.lastError = "cancelled";
      this.emit();
    });
    room.onMessage("pong", () => {});
    // o nó do jogo vai desligar (atualização): reconecta numa sala nova
    room.onMessage("migrate", (m: { matchId: string }) => {
      this.matchId = m.matchId;
      this.migrating = true;
    });
    room.onMessage("chat", (m: RoomChat) => {
      this.chat = [...this.chat.slice(-49), m];
      if (m.from !== this.myId) play("chat");
      this.chatListeners.forEach((l) => l(m));
    });
    room.onLeave((code) => {
      this.disconnected = (this.migrating || (code !== 1000 && code !== 4000)) && !this.ended;
      this.emit();
    });
  }

  send(action: Action): void {
    this.lastError = null;
    play(action.type === "buy" ? "buy" : action.type === "sell" ? "sell" : action.type === "reroll" ? "reroll" : action.type === "buyXp" ? "xp" : "click");
    this.room.send("action", action);
  }

  sendChat(text: string): void {
    this.room.send("chat", { text });
  }

  onChat(l: (m: RoomChat) => void): () => void {
    this.chatListeners.add(l);
    return () => this.chatListeners.delete(l);
  }

  /** começa já; `bots` completa as vagas livres com treinadores do jogo */
  start(bots = false): void {
    this.room.send("start", bots ? { bots: true } : undefined);
  }

  /** jogando sozinho: encerra o planejamento ("Lutar!") */
  ready(): void {
    this.room.send("ready");
  }

  /** id no lobby (o servidor avisa em "welcome"; muda para p1..pN quando a partida começa) */
  lobbyId: string | null = null;

  isHost(): boolean {
    return !!this.lobby?.host && this.lobby.host === this.lobbyId;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onRound(l: (r: RoundResult) => void): () => void {
    this.roundListeners.add(l);
    return () => this.roundListeners.delete(l);
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }
}

let current: GameStore | null = null;
export function gameStore(): GameStore | null {
  return current;
}
export function setGameStore(s: GameStore | null): void {
  current = s;
}
